import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithPopup, GoogleAuthProvider, signOut, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { sendMessage } from './services/agent';
import { askVoiceflow } from './services/voiceflow';
import { containsProfanity, getSafeDisplayName } from './utils/profanityFilter';
import { playManaged, pauseManaged, stopManaged, getManagedAudio } from './services/voiceService';
import * as documentStorage from './services/documentStorage';
import { setFirestore } from './services/usageTracking';
import './App.css';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import VoiceOptions from './pages/VoiceOptions';
import Subscription from './pages/Subscription';
import AdminDashboard from './pages/AdminDashboard';
import MockInterview from './pages/MockInterview';
import ResetPassword from './pages/ResetPassword';
import MessageFeedback from './components/MessageFeedback';
import FeedbackLink from './components/FeedbackLink';

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const provider = new GoogleAuthProvider();

// Initialize Firestore in usage tracking service
setFirestore(db);

// Export for use in other components
export { app, auth, db };

// Sidebar Navigation Component
function SidebarNav({ collapsed, darkMode, onToggleDarkMode, isAdmin, onSignOut }) {
  const location = useLocation();
  const isActive = (path) => location.pathname === path;
  
  return (
    <nav className="sidebar-nav">
      {/* Theme Toggle - First item in menu */}
      <button 
        onClick={onToggleDarkMode} 
        className={`sidebar-nav-item ${collapsed ? 'collapsed' : ''}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: collapsed ? '0' : '12px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'inherit'
        }}
        title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        {darkMode ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="5"/>
            <line x1="12" y1="1" x2="12" y2="3"/>
            <line x1="12" y1="21" x2="12" y2="23"/>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
            <line x1="1" y1="12" x2="3" y2="12"/>
            <line x1="21" y1="12" x2="23" y2="12"/>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
          </svg>
        )}
      </button>
      <Link to="/app" className={`sidebar-nav-item ${isActive('/app') || isActive('/app/') ? 'active' : ''} ${collapsed ? 'collapsed' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: collapsed ? '0' : '12px', justifyContent: collapsed ? 'center' : 'flex-start' }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        {!collapsed && <span>Chat</span>}
      </Link>
      <Link to="/app/mock-interview" className={`sidebar-nav-item ${isActive('/app/mock-interview') ? 'active' : ''} ${collapsed ? 'collapsed' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: collapsed ? '0' : '12px', justifyContent: collapsed ? 'center' : 'flex-start' }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
          <line x1="12" y1="19" x2="12" y2="23"/>
          <line x1="8" y1="23" x2="16" y2="23"/>
        </svg>
        {!collapsed && <span>Mock Interview</span>}
      </Link>
      <Link to="/app/settings" className={`sidebar-nav-item ${isActive('/app/settings') ? 'active' : ''} ${collapsed ? 'collapsed' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: collapsed ? '0' : '12px', justifyContent: collapsed ? 'center' : 'flex-start' }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
        {!collapsed && <span>Settings</span>}
      </Link>
      <Link to="/app/voice-options" className={`sidebar-nav-item ${isActive('/app/voice-options') ? 'active' : ''} ${collapsed ? 'collapsed' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: collapsed ? '0' : '12px', justifyContent: collapsed ? 'center' : 'flex-start' }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
          <line x1="12" y1="19" x2="12" y2="23"/>
          <line x1="8" y1="23" x2="16" y2="23"/>
        </svg>
        {!collapsed && <span>Voice Options</span>}
      </Link>
      <Link to="/app/subscription" className={`sidebar-nav-item ${isActive('/app/subscription') ? 'active' : ''} ${collapsed ? 'collapsed' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: collapsed ? '0' : '12px', justifyContent: collapsed ? 'center' : 'flex-start' }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
          <line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
        {!collapsed && <span>Pricing</span>}
      </Link>
      <button onClick={onSignOut} className={`sidebar-nav-item ${collapsed ? 'collapsed' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: collapsed ? '0' : '12px', justifyContent: collapsed ? 'center' : 'flex-start', width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
          <polyline points="16 17 21 12 16 7"/>
          <line x1="21" y1="12" x2="9" y2="12"/>
        </svg>
        {!collapsed && <span>Sign Out</span>}
      </button>
      {/* Admin link - only visible to admins, appears after Sign Out */}
      {isAdmin && (
        <Link to="/app/admin" className={`sidebar-nav-item ${isActive('/app/admin') ? 'active' : ''} ${collapsed ? 'collapsed' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: collapsed ? '0' : '12px', justifyContent: collapsed ? 'center' : 'flex-start' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
          </svg>
          {!collapsed && <span>Admin</span>}
        </Link>
      )}
    </nav>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : true;
  });
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [selectedVoiceName, setSelectedVoiceName] = useState(() => {
    const voiceId = localStorage.getItem('fidora_selected_voice') || 'nova';
    const voiceNames = {
      'nova': 'Nova',
      'sage': 'Atlas',    // Atlas uses sage voice ID
      'george': 'Sage',   // Sage uses george voice ID
      'marcus': 'Phoenix',
      'sarah': 'Aurora',
      'maya': 'Catalyst'
    };
    return voiceNames[voiceId] || 'Nova';
  });
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const autoSubmitTimeoutRef = useRef(null);
  const lastSpeechTimeRef = useRef(null);
  const [welcomeMessage, setWelcomeMessage] = useState(null);
  const [loadingWelcome, setLoadingWelcome] = useState(false);
  const [accentColor, setAccentColor] = useState(() => {
    return localStorage.getItem('fidora_accent_color') || 'purple';
  });
  const [showUserMessageMenu, setShowUserMessageMenu] = useState(null); // Message index
  const [subscriptionTier, setSubscriptionTier] = useState('free'); // Subscription tier status
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    const saved = localStorage.getItem('fidora_voice_enabled');
    return saved ? JSON.parse(saved) : false; // Default to off
  });
  const [isCoachSpeaking, setIsCoachSpeaking] = useState(false);
  const coachAudioRef = useRef(null);
  const fileInputRef = useRef(null);

  // Get coach avatar image based on selected voice
  const getCoachAvatar = () => {
    const voiceId = localStorage.getItem('fidora_selected_voice') || 'nova';
    const avatarMap = {
      'nova': '/jessica.png',
      'sage': '/george2.png',  // Atlas uses sage voice ID with george2.png avatar
      'george': '/roger.png',  // Sage uses george voice ID with roger.png avatar
      'marcus': '/bill.png',
      'sarah': '/rachel.png',
      'maya': '/amelia.png'
    };
    return avatarMap[voiceId] || '/jessica.png';
  };

  // Load subscription tier from Firestore
  const loadSubscriptionTier = async () => {
    if (!user?.uid) return;
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const data = userDoc.data();
        setSubscriptionTier(data.subscriptionTier || 'free');
      } else {
        setSubscriptionTier('free');
      }
    } catch (error) {
      console.error('Error loading subscription tier:', error);
      setSubscriptionTier('free');
    }
  };

  // Load initial greeting from Firebase coaching_prompts collection
  const loadWelcomeMessage = async () => {
    setLoadingWelcome(true);
    try {
      // Try to load the Greeting block from coaching_prompts
      const greetingDocRef = doc(db, 'coaching_prompts', 'Greeting');
      const greetingDoc = await getDoc(greetingDocRef);
      
      if (greetingDoc.exists()) {
        const greetingData = greetingDoc.data();
        // If there's a prompt, use it; otherwise use a default greeting
        if (greetingData.prompt) {
          // Send a greeting message to get the proper AI response
          try {
            const userName = getSafeDisplayName(user.displayName) || user.email?.split('@')[0] || 'there';
            const greetingResponse = await sendMessage('hello', [], userName);
            setWelcomeMessage(greetingResponse);
          } catch (error) {
            console.error('Error getting greeting response:', error);
            // Fallback to default
            setWelcomeMessage(`Hi ${getSafeDisplayName(user.displayName) || 'there'}! I'm ${selectedVoiceName}, your AI career coach. What would you like to work on today?`);
          }
        } else {
          // Fallback if no prompt in the document
          setWelcomeMessage(`Hi ${getSafeDisplayName(user.displayName) || 'there'}! I'm ${selectedVoiceName}, your AI career coach. What would you like to work on today?`);
        }
      } else {
        // If Greeting block doesn't exist, send a message to initialize the conversation
        try {
          const userName = getSafeDisplayName(user.displayName) || user.email?.split('@')[0] || 'there';
          const greetingResponse = await sendMessage('hello', [], userName);
          setWelcomeMessage(greetingResponse);
        } catch (error) {
          console.error('Error getting greeting response:', error);
          setWelcomeMessage(`Hi ${getSafeDisplayName(user.displayName) || 'there'}! I'm ${selectedVoiceName}, your AI career coach. What would you like to work on today?`);
        }
      }
    } catch (error) {
      console.error('Error loading welcome message:', error);
      // Fallback to a basic greeting
      setWelcomeMessage(`Hi ${getSafeDisplayName(user.displayName) || 'there'}! I'm ${selectedVoiceName}, your AI career coach. What would you like to work on today?`);
    } finally {
      setLoadingWelcome(false);
    }
  };

  // Load subscription tier when user changes
  useEffect(() => {
    if (user?.uid) {
      loadSubscriptionTier();
    } else {
      setSubscriptionTier('free');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Load welcome message from Firebase coaching_prompts when user is authenticated
  useEffect(() => {
    if (user && messages.length === 0 && !welcomeMessage && !loadingWelcome) {
      loadWelcomeMessage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, messages.length, welcomeMessage, loadingWelcome]);

  // Monitor auth state and check admin collection
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setCheckingAuth(true);
      
      if (!currentUser) {
        setUser(null);
        setIsAdmin(false);
        setCheckingAuth(false);
        return;
      }

      // Check if user is in admin collection
      try {
        const adminDocRef = doc(db, 'admins', currentUser.uid);
        const adminDoc = await getDoc(adminDocRef);
        
        if (adminDoc.exists() && adminDoc.data().isAdmin === true) {
          setUser(currentUser);
          setIsAdmin(true);
          console.log('✅ Admin access granted for:', currentUser.email);
          // Load subscription tier after user is set
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const data = userDoc.data();
            setSubscriptionTier(data.subscriptionTier || 'free');
          }
        } else {
          // User is not in admin collection - deny access
          setUser(null);
          setIsAdmin(false);
          await signOut(auth);
          alert('⛔ Access Denied. Only administrators can access this application.');
          console.log('❌ Admin access denied for:', currentUser.email);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setUser(null);
        setIsAdmin(false);
        await signOut(auth);
      }
      
      setCheckingAuth(false);
    });
    
    return () => unsubscribe();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Apply dark mode
  useEffect(() => {
    document.body.className = darkMode ? 'dark-mode' : 'light-mode';
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Update voice name when localStorage changes
  useEffect(() => {
    const updateVoiceName = () => {
      const voiceId = localStorage.getItem('fidora_selected_voice') || 'nova';
      const voiceNames = {
        'nova': 'Nova',
        'sage': 'Atlas',    // Atlas uses sage voice ID
        'george': 'Sage',   // Sage uses george voice ID
        'marcus': 'Phoenix',
        'sarah': 'Aurora',
        'maya': 'Catalyst'
      };
      setSelectedVoiceName(voiceNames[voiceId] || 'Nova');
    };
    updateVoiceName();
    window.addEventListener('storage', updateVoiceName);
    return () => window.removeEventListener('storage', updateVoiceName);
  }, []);

  // Listen for accent color changes
  useEffect(() => {
    const handleAccentColorChange = (e) => {
      setAccentColor(e.detail);
    };
    window.addEventListener('accentColorChange', handleAccentColorChange);
    return () => window.removeEventListener('accentColorChange', handleAccentColorChange);
  }, []);

  // Accent color map
  const accentColorMap = {
    purple: { gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', primary: '#6366f1', textColor: '#ffffff' },
    blue: { gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', primary: '#3b82f6', textColor: '#ffffff' },
    green: { gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', primary: '#10b981', textColor: '#ffffff' },
    orange: { gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', primary: '#f97316', textColor: '#ffffff' },
    pink: { gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)', primary: '#ec4899', textColor: '#ffffff' },
    teal: { gradient: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)', primary: '#14b8a6', textColor: '#ffffff' }
  };

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';

      recognitionInstance.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        // If user starts speaking (has interim results), stop coach audio immediately
        // Note: isCoachSpeaking is accessed via closure, don't add to deps to avoid re-initialization
        const currentCoachSpeaking = isCoachSpeaking;
        if (interimTranscript && currentCoachSpeaking) {
          stopManaged();
          setIsCoachSpeaking(false);
        }

        // Update last speech time
        lastSpeechTimeRef.current = Date.now();

        // Clear existing auto-submit timeout
        if (autoSubmitTimeoutRef.current) {
          clearTimeout(autoSubmitTimeoutRef.current);
          autoSubmitTimeoutRef.current = null;
        }

        if (finalTranscript) {
          setInputMessage(prev => {
            const cleaned = prev.replace(/\[Listening...\]$/, '').trim();
            const newText = cleaned + ' ' + finalTranscript;
            // Set new timeout for auto-submit after 4 seconds of silence
            autoSubmitTimeoutRef.current = setTimeout(() => {
              handleAutoSubmit();
            }, 4000);
            return newText;
          });
        } else if (interimTranscript) {
          setInputMessage(prev => {
            const base = prev.replace(/\[Listening...\]$/, '').trim();
            const newText = base + ' ' + interimTranscript + ' [Listening...]';
            // Set new timeout for auto-submit after 4 seconds of silence
            autoSubmitTimeoutRef.current = setTimeout(() => {
              handleAutoSubmit();
            }, 4000);
            return newText;
          });
        }
      };

      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'no-speech') {
          // Auto-restart if no speech detected (only if actively recording)
          // Check isRecording via closure to avoid re-initialization
          const currentlyRecording = isRecording;
          if (currentlyRecording) {
            setTimeout(() => {
              try {
                if (recognitionRef.current && currentlyRecording) {
                  recognitionRef.current.start();
                }
              } catch (e) {
                console.error('Error restarting recognition:', e);
              }
            }, 100);
          }
        } else if (event.error === 'not-allowed') {
          alert('Microphone permission denied. Please allow microphone access.');
          setIsRecording(false);
        }
      };

      recognitionInstance.onend = () => {
        // Check isRecording via closure to avoid re-initialization
        const currentlyRecording = isRecording;
        if (currentlyRecording && recognitionRef.current) {
          setTimeout(() => {
            try {
              if (recognitionRef.current && currentlyRecording) {
                recognitionRef.current.start();
              }
            } catch (e) {
              console.error('Error restarting recognition:', e);
              setIsRecording(false);
            }
          }, 100);
        }
      };

      recognitionRef.current = recognitionInstance;
      setRecognition(recognitionInstance);
    }
  }, []);

  // Toggle recording
  const toggleRecording = () => {
    if (!recognition) {
      alert('Speech recognition is not supported in your browser.');
      return;
    }

    if (isRecording) {
      recognition.stop();
      setIsRecording(false);
      // Clear auto-submit timeout
      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current);
        autoSubmitTimeoutRef.current = null;
      }
      const messageToSubmit = inputMessage.replace(/\[Listening...\]$/, '').trim();
      setInputMessage(messageToSubmit);
      // If there's a message, submit it
      if (messageToSubmit) {
        submitMessage(messageToSubmit);
      } else {
        // Stop coach audio when user stops recording without a message
        stopManaged();
        setIsCoachSpeaking(false);
      }
    } else {
      // Pause coach audio when user starts speaking
      if (isCoachSpeaking) {
        pauseManaged();
      }
      try {
        recognition.start();
        setIsRecording(true);
        lastSpeechTimeRef.current = Date.now();
        setInputMessage(prev => {
          const cleaned = prev.replace(/\[Listening...\]$/, '').trim();
          return cleaned + ' [Listening...]';
        });
        // Set initial timeout for auto-submit after 4 seconds
        autoSubmitTimeoutRef.current = setTimeout(() => {
          handleAutoSubmit();
        }, 4000);
      } catch (error) {
        console.error('Error starting recognition:', error);
        alert('Failed to start recording. Please try again.');
      }
    }
  };

  // Toggle voice on/off
  const toggleVoice = () => {
    const newVoiceState = !voiceEnabled;
    setVoiceEnabled(newVoiceState);
    localStorage.setItem('fidora_voice_enabled', JSON.stringify(newVoiceState));
    
    // If turning voice off, stop any currently playing audio
    if (!newVoiceState) {
      stopManaged();
      setIsCoachSpeaking(false);
    }
  };

  // Sign in with Google
  const handleSignIn = async () => {
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error('Error signing in:', error);
      alert('Failed to sign in. Please try again.');
    }
  };

  // Sign out
  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setMessages([]);
      setUser(null);
      setIsAdmin(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Auto-submit message after 4 seconds of silence
  const handleAutoSubmit = () => {
    const messageToSubmit = inputMessage.replace(/\[Listening...\]$/, '').trim();
    if (messageToSubmit && !isLoading && isRecording) {
      // Stop recording first
      if (recognition) {
        recognition.stop();
        setIsRecording(false);
      }
      // Clear the timeout
      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current);
        autoSubmitTimeoutRef.current = null;
      }
      // Submit the message
      submitMessage(messageToSubmit);
    }
  };

  // Form submit handler
  const handleSendMessage = async (e) => {
    e.preventDefault();
    const messageToSubmit = inputMessage.replace(/\[Listening...\]$/, '').trim();
    if (!messageToSubmit || isLoading) return;
    
    // Clear auto-submit timeout if it exists
    if (autoSubmitTimeoutRef.current) {
      clearTimeout(autoSubmitTimeoutRef.current);
      autoSubmitTimeoutRef.current = null;
    }
    
    // Stop recording if active
    if (isRecording && recognition) {
      recognition.stop();
      setIsRecording(false);
    }
    
    submitMessage(messageToSubmit);
  };

  // Submit message helper function
  const submitMessage = async (userMessage) => {
    if (!userMessage || isLoading) return;
    
    // Stop any currently playing coach audio when user sends a message
    if (isCoachSpeaking) {
      stopManaged();
      setIsCoachSpeaking(false);
    }
    
    // Check for profanity in chat message
    if (containsProfanity(userMessage)) {
      alert('⚠️ Please use appropriate language. Your message contains inappropriate content.');
      return;
    }
    
    setInputMessage('');
    
    // If this is the first message and we have a welcome message, add it to messages first
    let messagesToAdd = [];
    if (messages.length === 0 && welcomeMessage) {
      messagesToAdd.push({ role: 'assistant', content: welcomeMessage });
      setWelcomeMessage(null); // Clear welcome message so it doesn't show again
    }
    
    // Add user message to chat
    const newUserMessage = { role: 'user', content: userMessage };
    messagesToAdd.push(newUserMessage);
    setMessages(prev => [...prev, ...messagesToAdd]);
    setIsLoading(true);

    try {
      // Build history for API call (include welcome message if it was just added)
      const currentMessages = messages.length === 0 && welcomeMessage 
        ? [{ role: 'assistant', content: welcomeMessage }, { role: 'user', content: userMessage }]
        : [...messages, { role: 'user', content: userMessage }];
      
      const history = currentMessages.slice(0, -1).map(msg => ({
        role: msg.role,
        content: msg.content,
      }));

      // Get AI response - pass userName for personalization
      const userName = getSafeDisplayName(user?.displayName) || user?.email?.split('@')[0] || null;
      const reply = await sendMessage(userMessage, history, userName);
      
      // Add AI response to chat
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      
      // Play assistant message as voice if voice is enabled
      // Only play if user is not currently recording (to avoid interruption loop)
      if (voiceEnabled && reply && !isRecording) {
        try {
          const voiceId = localStorage.getItem('fidora_selected_voice') || 'nova';
          setIsCoachSpeaking(true);
          const audio = await playManaged(reply, voiceId);
          coachAudioRef.current = audio;
          
          // Set up event handlers
          audio.onended = () => {
            setIsCoachSpeaking(false);
            coachAudioRef.current = null;
            // Auto-restart recording after coach finishes speaking for continuous conversation
            if (recognition && !isRecording) {
              setTimeout(() => {
                try {
                  recognition.start();
                  setIsRecording(true);
                  lastSpeechTimeRef.current = Date.now();
                  setInputMessage('[Listening...]');
                  // Set timeout for auto-submit
                  autoSubmitTimeoutRef.current = setTimeout(() => {
                    handleAutoSubmit();
                  }, 4000);
                } catch (e) {
                  console.error('Error restarting recognition after coach:', e);
                }
              }, 500); // Small delay to ensure audio finished
            }
          };
          
          audio.onpause = () => {
            // Audio was paused (likely by user interruption)
          };
          
          audio.onerror = () => {
            setIsCoachSpeaking(false);
            coachAudioRef.current = null;
          };
        } catch (error) {
          console.error('Error playing voice:', error);
          setIsCoachSpeaking(false);
        }
      } else if (!voiceEnabled && !isRecording && recognition) {
        // If voice is disabled, auto-restart recording after coach responds
        setTimeout(() => {
          try {
            recognition.start();
            setIsRecording(true);
            lastSpeechTimeRef.current = Date.now();
            setInputMessage('[Listening...]');
            // Set timeout for auto-submit
            autoSubmitTimeoutRef.current = setTimeout(() => {
              handleAutoSubmit();
            }, 4000);
          } catch (e) {
            console.error('Error restarting recognition after response:', e);
          }
        }, 500);
      }
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: '❌ Sorry, I encountered an error. Please try again.' },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle dark mode
  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  // Handle accent color change
  const handleAccentColorChange = (colorId) => {
    setAccentColor(colorId);
    localStorage.setItem('fidora_accent_color', colorId);
    window.dispatchEvent(new CustomEvent('accentColorChange', { detail: colorId }));
  };

  // Clear input field
  const clearInput = () => {
    setInputMessage('');
  };

  // Clear all chat messages
  const clearChat = () => {
    if (window.confirm('Are you sure you want to clear all messages? This will start a new conversation.')) {
      setMessages([]);
      setWelcomeMessage(null);
      setInputMessage('');
      // Reload welcome message after clearing
      if (user) {
        loadWelcomeMessage();
      }
    }
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !user?.uid) return;

    // Safety: Maximum file size limit (5MB per file)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > MAX_FILE_SIZE) {
      alert(`File size exceeds the maximum limit of 5MB. Please upload a smaller file.`);
      return;
    }

    // Safety: Blocked file types (images, videos, code, scripts, executables)
    const blockedExtensions = [
      // Images
      '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg', '.webp', '.ico', '.tiff', '.tif',
      // Videos
      '.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv', '.m4v', '.mpg', '.mpeg',
      // Code files
      '.js', '.jsx', '.ts', '.tsx', '.py', '.java', '.cpp', '.c', '.h', '.cs', '.php', '.rb', '.go', '.rs',
      '.html', '.htm', '.css', '.scss', '.sass', '.less', '.xml', '.json', '.yaml', '.yml',
      // Scripts and executables
      '.sh', '.bat', '.cmd', '.ps1', '.exe', '.dll', '.so', '.dylib', '.bin',
      // Archives (could contain anything)
      '.zip', '.rar', '.7z', '.tar', '.gz', '.bz2',
      // Office files (only PDF, DOC, DOCX, TXT, RTF, ODT allowed - rest blocked)
      '.xls', '.xlsx', '.ppt', '.pptx'
    ];

    // Safety: Only allow specific document types
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.rtf', '.odt'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    // Check if file is blocked
    if (blockedExtensions.includes(fileExtension) && !allowedExtensions.includes(fileExtension)) {
      alert('File type not allowed. For safety, we only accept: PDF, DOC, DOCX, TXT, RTF, or ODT documents. Images, videos, code files, and scripts are not permitted.');
      return;
    }

    // Check MIME type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'application/rtf',
      'application/vnd.oasis.opendocument.text'
    ];
    
    // Additional safety: Check if MIME type suggests blocked content
    const blockedMimePatterns = [
      'image/', 'video/', 'audio/',
      'application/x-executable', 'application/x-sharedlib',
      'application/x-javascript', 'application/javascript',
      'text/javascript', 'application/x-python',
      'application/x-compressed', 'application/zip',
      'application/x-rar-compressed', 'application/x-7z-compressed'
    ];
    
    const isBlockedMime = blockedMimePatterns.some(pattern => file.type.startsWith(pattern));
    if (isBlockedMime || (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension))) {
      alert('File type not allowed. For safety, we only accept: PDF, DOC, DOCX, TXT, RTF, or ODT documents. Images, videos, code files, and scripts are not permitted.');
      return;
    }

    try {
      // Read file content
      let content = '';
      if (file.type === 'text/plain') {
        // Read as text
        content = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsText(file);
        });
      } else {
        // Read as base64 for binary files
        content = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const base64 = e.target.result.split(',')[1];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }

      // Save to document storage
      const subscriptionTierValue = subscriptionTier || 'free';
      await documentStorage.saveDocument(user.uid, file, content, subscriptionTierValue);
      
      // Show success message
      const successMsg = `✅ Document "${file.name}" uploaded successfully! It's now saved in your document manager and available to your coach.`;
      setMessages(prev => [...prev, { role: 'assistant', content: successMsg }]);
      
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      const errorMsg = `❌ Error uploading file: ${error.message || 'Please try again.'}`;
      setMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
    }
  };

  // Toggle user dropdown
  const toggleUserDropdown = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showUserDropdown && !e.target.closest('.sidebar-user') && !e.target.closest('.user-dropdown')) {
        setShowUserDropdown(false);
      }
    };
    if (showUserDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showUserDropdown]);

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.1rem',
        color: '#666'
      }}>
        Checking authentication...
      </div>
    );
  }

  // Show landing page if not signed in
  if (!user || !isAdmin) {
    return (
      <Router>
        <Routes>
          <Route path="/" element={<Landing darkMode={darkMode} onToggleTheme={toggleDarkMode} />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    );
  }

  // Main app interface - only accessible to admins
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Navigate to="/app" replace />} />
        <Route path="/login" element={<Navigate to="/app" replace />} />
        <Route path="/app/*" element={
          <div className="app-layout">
            <aside className={`app-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`} style={{ width: sidebarCollapsed ? '80px' : '260px', minWidth: sidebarCollapsed ? '80px' : '260px' }}>
              <div className="sidebar-header">
                <button
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    padding: '12px',
                    marginBottom: '0',
                    justifyContent: sidebarCollapsed ? 'center' : 'space-between'
                  }}
                  title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <img 
                      src="/fidora_fav1.png" 
                      alt="Fidora" 
                      style={{ 
                        width: 72, 
                        height: 72, 
                        objectFit: 'contain',
                        flexShrink: 0
                      }} 
                    />
                    {!sidebarCollapsed && (
                      <span className="sidebar-logo" style={{ fontSize: '1.25rem', fontWeight: 600, color: darkMode ? '#818cf8' : '#6366f1' }}>
                        Fidora
                      </span>
                    )}
                  </div>
                  {!sidebarCollapsed && (
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      style={{ 
                        color: darkMode ? '#94a3b8' : '#64748b',
                        flexShrink: 0,
                        transition: 'transform 0.2s ease'
                      }}
                    >
                      <path d="M15 18l-6-6 6-6"/>
                    </svg>
                  )}
                </button>
              </div>
              
              <SidebarNav collapsed={sidebarCollapsed} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} isAdmin={isAdmin} onSignOut={handleSignOut} />
              
              <div className="sidebar-footer">
                <div className="sidebar-user" onClick={toggleUserDropdown} style={{ display: 'flex', alignItems: 'center', gap: sidebarCollapsed ? '0' : '12px', justifyContent: sidebarCollapsed ? 'center' : 'flex-start', padding: sidebarCollapsed ? '12px' : '12px 16px' }}>
                  <div className="sidebar-avatar">{getSafeDisplayName(user.displayName)?.[0]?.toUpperCase() || 'U'}</div>
                  {!sidebarCollapsed && (
                    <div className="sidebar-user-info">
                      <div className="sidebar-user-name">{getSafeDisplayName(user.displayName)}</div>
                      <div className="sidebar-user-email" style={{ 
                        textTransform: 'capitalize', 
                        fontSize: '0.85rem', 
                        opacity: 0.8,
                        fontWeight: 500,
                        color: subscriptionTier === 'free' ? 'inherit' : subscriptionTier === 'advisor' ? '#10b981' : subscriptionTier === 'coach' ? '#3b82f6' : '#8b5cf6'
                      }}>
                        {subscriptionTier === 'free' ? 'Free' : subscriptionTier === 'advisor' ? 'Advisor' : subscriptionTier === 'coach' ? 'Coach' : subscriptionTier === 'mentor' ? 'Mentor' : subscriptionTier}
                      </div>
                    </div>
                  )}
                </div>
                
                {showUserDropdown && (
                  <div className="user-dropdown">
                    <button className="user-dropdown-item" onClick={() => { setShowUserDropdown(false); }}>
                      Account Settings
                    </button>
                  </div>
                )}
              </div>
            </aside>
            
            <main className="app-main-content">
              {/* Global Feedback Button - Visible on all app pages */}
              <FeedbackLink user={user} darkMode={darkMode} />
              
              <Routes>
                <Route
                  index
                  element={(
                    <div className="chat-container">
                      <div className="messages-list">
                        {messages.length === 0 && (
                          <div className="welcome-message">
                            {loadingWelcome ? (
                              <>
                                <h2>Welcome to Fidora!</h2>
                                <p>Loading your personalized greeting...</p>
                              </>
                            ) : welcomeMessage ? (
                              <>
                                <div className="message assistant">
                                  <div className="message-avatar">
                                    <img 
                                      src={getCoachAvatar()} 
                                      alt={selectedVoiceName}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        borderRadius: '50%'
                                      }}
                                    />
                                  </div>
                                  <div className="message-content">
                                    <div className="message-text">{welcomeMessage}</div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                                <h2>Welcome to Fidora!</h2>
                                <p>Your AI career coach is ready to help.</p>
                                <p>Ask me anything to get started!</p>
                              </>
                            )}
                          </div>
                        )}
                        {messages.map((msg, index) => (
                          <div key={index} className={`message ${msg.role}`}>
                            {msg.role === 'assistant' && (
                              <div className="message-avatar">
                                <img 
                                  src={getCoachAvatar()} 
                                  alt={selectedVoiceName}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '50%'
                                  }}
                                />
                              </div>
                            )}
                            <div 
                              className="message-content"
                              style={msg.role === 'user' ? {
                                background: accentColorMap[accentColor]?.gradient || accentColorMap.purple.gradient,
                                color: accentColorMap[accentColor]?.textColor || '#ffffff',
                                borderLeft: 'none',
                                boxShadow: `0 2px 8px ${accentColorMap[accentColor]?.primary || accentColorMap.purple.primary}40`,
                                position: 'relative'
                              } : {
                                background: 'transparent',
                                borderLeft: 'none',
                                boxShadow: 'none'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', width: '100%' }}>
                                <div className="message-text" style={{ flex: 1, wordBreak: 'break-word' }}>{msg.content}</div>
                                {msg.role === 'user' && (
                                  <div style={{ flexShrink: 0, marginTop: '4px', position: 'relative' }}>
                                    <button
                                      onClick={() => setShowUserMessageMenu(showUserMessageMenu === index ? null : index)}
                                      style={{
                                        background: 'rgba(0, 0, 0, 0.2)',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        padding: '4px 8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'background 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)'}
                                      onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.2)'}
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={accentColorMap[accentColor]?.textColor || 'white'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="1"></circle>
                                        <circle cx="19" cy="12" r="1"></circle>
                                        <circle cx="5" cy="12" r="1"></circle>
                                      </svg>
                                    </button>
                                    {showUserMessageMenu === index && (
                                      <>
                                        <div 
                                          style={{
                                            position: 'fixed',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            zIndex: 999
                                          }}
                                          onClick={() => setShowUserMessageMenu(null)}
                                        />
                                        <div style={{
                                          position: 'absolute',
                                          top: '32px',
                                          right: '0',
                                          background: darkMode ? '#1f2937' : 'white',
                                          border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                                          borderRadius: '8px',
                                          boxShadow: 'none',
                                          minWidth: '200px',
                                          zIndex: 1000,
                                          padding: '8px'
                                        }}>
                                          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: darkMode ? '#9ca3af' : '#6b7280', padding: '8px 12px', marginBottom: '4px' }}>
                                            Change Accent Color
                                          </div>
                                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                                            {Object.entries(accentColorMap).map(([colorId, color]) => (
                                              <button
                                                key={colorId}
                                                onClick={() => {
                                                  handleAccentColorChange(colorId);
                                                  setShowUserMessageMenu(null);
                                                }}
                                                style={{
                                                  width: '32px',
                                                  height: '32px',
                                                  borderRadius: '50%',
                                                  background: color.gradient,
                                                  border: accentColor === colorId ? '2px solid white' : 'none',
                                                  cursor: 'pointer',
                                                  boxShadow: accentColor === colorId ? `0 0 0 2px ${darkMode ? '#1f2937' : 'white'}` : 'none'
                                                }}
                                                title={colorId.charAt(0).toUpperCase() + colorId.slice(1)}
                                              />
                                            ))}
                                          </div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                              {msg.role === 'assistant' && (
                                <MessageFeedback 
                                  messageContent={msg.content} 
                                  user={user} 
                                  darkMode={darkMode} 
                                />
                              )}
                            </div>
                          </div>
                        ))}
                        {isLoading && (
                          <div className="message assistant">
                            <div className="message-avatar">
                              <img 
                                src={getCoachAvatar()} 
                                alt={selectedVoiceName}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '50%'
                                }}
                              />
                            </div>
                            <div className="message-content">
                              <div className="typing-indicator">
                                <span></span>
                                <span></span>
                                <span></span>
                              </div>
                            </div>
                          </div>
                        )}
                        <div ref={messagesEndRef} />
                      </div>

                      <form onSubmit={handleSendMessage} className="input-form">
                        {/* Trash can - clear all chat */}
                        <button
                          type="button"
                          onClick={clearChat}
                          className="input-action-btn"
                          title="Clear all chat messages"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit' }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          </svg>
                        </button>
                        
                        {/* Paperclip - attachments */}
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileUpload}
                          accept=".pdf,.doc,.docx,.txt,.rtf,.odt"
                          style={{ display: 'none' }}
                        />
                        <button
                          type="button"
                          className="input-action-btn"
                          title="Upload document to resume manager"
                          onClick={() => fileInputRef.current?.click()}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit' }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                          </svg>
                        </button>
                        
                        {/* Text input */}
                        <input
                          type="text"
                          value={inputMessage.replace(/\[Listening...\]$/, '')}
                          onChange={(e) => setInputMessage(e.target.value)}
                          placeholder="What do you want to know?"
                          disabled={isLoading || isRecording}
                          className="message-input"
                        />
                        
                        {/* Microphone button */}
                        <button
                          type="button"
                          onClick={toggleRecording}
                          className={`voice-record-btn ${isRecording ? 'recording' : ''}`}
                          title={isRecording ? 'Stop recording' : 'Start voice recording'}
                        >
                          {isRecording ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="6" y="6" width="12" height="12" rx="2"/>
                            </svg>
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                              <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                              <line x1="12" y1="19" x2="12" y2="23"/>
                              <line x1="8" y1="23" x2="16" y2="23"/>
                            </svg>
                          )}
                        </button>
                        
                        {/* Voice On/Off Toggle */}
                        <button
                          type="button"
                          onClick={toggleVoice}
                          className="input-action-btn"
                          title={voiceEnabled ? 'Voice On - Click to turn off' : 'Voice Off - Click to turn on'}
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            padding: '8px', 
                            background: 'transparent', 
                            border: 'none', 
                            cursor: 'pointer', 
                            color: voiceEnabled ? (darkMode ? '#10b981' : '#059669') : 'inherit',
                            opacity: voiceEnabled ? 1 : 0.7
                          }}
                        >
                          {voiceEnabled ? (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 5L6 9H2v6h4l5 4V5z"/>
                              <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
                            </svg>
                          ) : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 5L6 9H2v6h4l5 4V5z"/>
                              <line x1="23" y1="9" x2="17" y2="15"/>
                              <line x1="17" y1="9" x2="23" y2="15"/>
                            </svg>
                          )}
                        </button>
                        
                        {/* Send button */}
                        <button type="submit" disabled={isLoading || !inputMessage.trim() || isRecording} className="send-btn">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="22" y1="2" x2="11" y2="13"/>
                            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                          </svg>
                        </button>
                      </form>
                    </div>
                  )}
                />
                <Route path="profile" element={<Profile user={user} darkMode={darkMode} />} />
                        <Route path="settings" element={<Settings darkMode={darkMode} onToggleTheme={toggleDarkMode} user={user} />} />
                        <Route path="voice-options" element={<VoiceOptions darkMode={darkMode} user={user} />} />
                        <Route path="subscription" element={<Subscription user={user} darkMode={darkMode} />} />
                <Route path="admin" element={<AdminDashboard user={user} darkMode={darkMode} />} />
                <Route path="mock-interview" element={<MockInterview user={user} darkMode={darkMode} selectedCoach={selectedVoiceName} onSelectCoach={(coach) => {
                  // Update selected voice when coach is selected in mock interview
                  const voiceIdMap = {
                    'Nova': 'nova',
                    'Atlas': 'sage',
                    'Sage': 'george',
                    'Phoenix': 'marcus',
                    'Aurora': 'sarah',
                    'Catalyst': 'maya'
                  };
                  const voiceId = voiceIdMap[coach] || 'nova';
                  localStorage.setItem('fidora_selected_voice', voiceId);
                  setSelectedVoiceName(coach);
                }} />} />
              </Routes>
            </main>
          </div>
        } />
      </Routes>
    </Router>
  );
}

export default App;

